import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatFileSize, resolveAttachmentFilename } from '@/services/encompassDocumentService';
import {
  downloadAnalyzerEfolderFiles, efolderUnavailableReason, loadAnalyzerEfolder,
  type AnalyzerEfolder
} from '@/services/analyzerEfolderService';

interface AnalyzerEfolderDialogProps {
  loanId: string;
  maxFiles: number;
  onFiles: (files: File[]) => void;
  onCancel: () => void;
  onClosed: () => void;
}

/** Mounted per selection request; closing or changing loans invalidates every pending read. */
export function AnalyzerEfolderDialog({ loanId, maxFiles, onFiles, onCancel, onClosed }: AnalyzerEfolderDialogProps) {
  const [folder, setFolder] = useState<AnalyzerEfolder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const operation = useRef<AbortController | null>(null);
  const id = useId();

  const load = useCallback(async () => {
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    setLoading(true);
    setFolder(null);
    setSelected(new Set());
    setError('');
    try {
      const result = await loadAnalyzerEfolder(loanId, controller.signal);
      if (!controller.signal.aborted) {
        setFolder(result);
      }
    } catch (caught) {
      if (!controller.signal.aborted) {
        setError(caught instanceof Error ? caught.message : 'Documents could not be loaded. Try again.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [loanId]);

  useEffect(() => {
    void load();
    return () => operation.current?.abort();
  }, [load]);

  const cancel = () => {
    operation.current?.abort();
    onCancel();
  };

  const add = async () => {
    if (importing || !folder || selected.size === 0 || selected.size > maxFiles) {
      return;
    }
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    setImporting(true);
    setError('');
    try {
      const files = await downloadAnalyzerEfolderFiles(folder, [...selected], maxFiles, controller.signal);
      if (!controller.signal.aborted) {
        onFiles(files);
      }
    } catch (caught) {
      if (!controller.signal.aborted) {
        setError(caught instanceof Error ? caught.message : 'Documents could not be added. Try again.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setImporting(false);
      }
    }
  };

  const visibleFolders = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (folder?.documents || []).map(doc => ({
      ...doc,
      attachments: doc.attachments.filter(file => [doc.title, file.title, resolveAttachmentFilename(file)]
        .some(value => value?.toLowerCase().includes(search)))
    })).filter(doc => doc.attachments.length > 0);
  }, [folder, query]);

  return (
    <Dialog open onOpenChange={open => {
      if (!open) {
        cancel();
      }
    }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto" onCloseAutoFocus={event => {
        event.preventDefault();
        onClosed();
      }}>
        <DialogHeader>
          <DialogTitle>eFolder documents</DialogTitle>
          <DialogDescription className="break-words">
            {folder ? `${folder.borrower} · Loan ${folder.loanNumber}. ` : ''}
            Select bank statement PDFs from Loan Center to add to this analysis.
          </DialogDescription>
        </DialogHeader>
        {loading ? <p role="status" className="text-sm text-muted-foreground">Loading eFolder documents…</p> : (
          <>
            {folder && (
              <>
                <div className="space-y-2">
                  <Label htmlFor={`${id}-search`}>Search eFolder documents</Label>
                  <Input id={`${id}-search`} value={query} disabled={importing} onChange={event => setQuery(event.target.value)} placeholder="Search file or folder names…" />
                </div>
                <p className="text-xs text-muted-foreground">PDF only · up to {maxFiles} more statements · 25 MB per file, 100 MB per import. File names must include the statement month and year.</p>
                <div className="max-h-[45dvh] space-y-4 overflow-y-auto rounded-md border p-3" aria-label="eFolder document list">
                  {visibleFolders.length === 0 && (
                    <p className="text-sm text-muted-foreground">{query ? 'No documents match your search.' : 'No current eFolder documents are available for this loan.'}</p>
                  )}
                  {visibleFolders.map(doc => (
                    <fieldset key={doc.id} disabled={importing} className="min-w-0 space-y-2">
                      <legend className="mb-2 break-words text-sm font-semibold">{doc.title}</legend>
                      {doc.attachments.map(file => {
                        const reason = efolderUnavailableReason(file);
                        const checked = selected.has(file.id);
                        const inputId = `${id}-${file.id}`;
                        return (
                          <div key={file.id} className="flex items-start gap-3 rounded-md p-2 hover:bg-muted/50">
                            <Checkbox id={inputId} checked={checked} className="mt-1" disabled={Boolean(reason) || importing || (!checked && selected.size >= maxFiles)}
                              aria-describedby={`${inputId}-detail`}
                              onCheckedChange={value => setSelected(previous => {
                                const next = new Set(previous);
                                if (value === true) {
                                  next.add(file.id);
                                } else {
                                  next.delete(file.id);
                                }
                                return next;
                              })} />
                            <div className="min-w-0 flex-1">
                              <Label htmlFor={inputId} className="block break-words leading-5">{resolveAttachmentFilename(file)}</Label>
                              <p id={`${inputId}-detail`} className="mt-1 break-words text-xs text-muted-foreground">{reason || formatFileSize(file.file_size ?? null)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </fieldset>
                  ))}
                </div>
              </>
            )}
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          </>
        )}
        <p role="status" aria-atomic="true" className="flex items-center gap-2 text-sm text-muted-foreground">
          {importing && <Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 motion-safe:animate-spin" />}
          <span>{importing ? `Importing ${selected.size} statement${selected.size === 1 ? '' : 's'}…` : `${selected.size} selected`}</span>
        </p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={cancel}>Cancel</Button>
          {!loading && !importing && <Button variant="outline" onClick={() => void load()}>Refresh documents</Button>}
          <Button disabled={loading || importing || !folder || selected.size === 0 || selected.size > maxFiles} onClick={() => void add()}>
            {importing ? 'Importing…' : 'Add selected statements'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
